import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Bot, Store, LayoutGrid, ChevronDown, Plus } from 'lucide-react';
import { Input, Button } from '../../ui';
import styles from './AssistantsSidebar.module.css';

export type AssistantCategory = 'project' | 'marketplace' | 'templates';

export interface AssistantsSidebarProps {
  selectedCategory: AssistantCategory;
  onCategoryChange: (category: AssistantCategory) => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  selectedProject?: string;
  onProjectChange?: (project: string) => void;
  categories: string[];
  selectedCategories: string[];
  onCategoriesChange: (categories: string[]) => void;
  createdByMe: boolean;
  onCreatedByMeChange: (value: boolean) => void;
  selectedCreator?: string;
  onCreatorChange?: (creator: string) => void;
}

export const AssistantsSidebar: React.FC<AssistantsSidebarProps> = ({
  selectedCategory,
  onCategoryChange,
  searchQuery,
  onSearchChange,
  selectedProject,
  onProjectChange,
  categories,
  selectedCategories,
  onCategoriesChange,
  createdByMe,
  onCreatedByMeChange,
  selectedCreator,
  onCreatorChange,
}) => {
  const navigate = useNavigate();

  const handleCreateAssistant = () => {
    console.log('Create button clicked - navigating to /assistants/create');
    navigate('/assistants/create');
  };

  return (
    <div className={styles.sidebar}>
      <div className={styles.header}>
        <div className={styles.headerTop}>
          <div>
            <h1 className={styles.title}>Assistants</h1>
            <p className={styles.subtitle}>Browse and chat with available AI assistants</p>
          </div>
          <Button
            type="button"
            variant="primary"
            size="sm"
            leftIcon={<Plus size={16} />}
            onClick={handleCreateAssistant}
          >
            Create
          </Button>
        </div>
      </div>

      <div className={styles.categories}>
        <h2 className={styles.sectionTitle}>CATEGORIES</h2>
        <nav className={styles.categoryNav}>
          <button
            className={`${styles.categoryItem} ${
              selectedCategory === 'project' ? styles.active : ''
            }`}
            onClick={() => onCategoryChange('project')}
          >
            <Bot size={18} />
            <span>Project Assistants</span>
          </button>
          <button
            className={`${styles.categoryItem} ${
              selectedCategory === 'marketplace' ? styles.active : ''
            }`}
            onClick={() => onCategoryChange('marketplace')}
          >
            <Store size={18} />
            <span>Marketplace</span>
          </button>
          <button
            className={`${styles.categoryItem} ${
              selectedCategory === 'templates' ? styles.active : ''
            }`}
            onClick={() => onCategoryChange('templates')}
          >
            <LayoutGrid size={18} />
            <span>Templates</span>
          </button>
        </nav>
      </div>

      <div className={styles.filters}>
        <h2 className={styles.sectionTitle}>FILTERS</h2>

        <div className={styles.filterGroup}>
          <Input
            isSearch
            placeholder="Search"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            fullWidth
          />
        </div>

        <div className={styles.filterGroup}>
          <button className={styles.filterHeader}>
            <ChevronDown size={14} />
            <span>PROJECT</span>
          </button>
          <div className={styles.filterContent}>
            <select
              className={styles.select}
              value={selectedProject}
              onChange={(e) => onProjectChange?.(e.target.value)}
            >
              <option value="">Project</option>
              <option value="bringiq">bringiq</option>
              <option value="demo">demo</option>
            </select>
          </div>
        </div>

        <div className={styles.filterGroup}>
          <button className={styles.filterHeader}>
            <ChevronDown size={14} />
            <span>CATEGORIES</span>
          </button>
          <div className={styles.filterContent}>
            <select
              className={styles.select}
              value={selectedCategories[0] || ''}
              onChange={(e) => onCategoriesChange(e.target.value ? [e.target.value] : [])}
            >
              <option value="">Categories</option>
              {categories.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className={styles.filterGroup}>
          <button className={styles.filterHeader}>
            <ChevronDown size={14} />
            <span>CREATED BY</span>
          </button>
          <div className={styles.filterContent}>
            <label className={styles.checkbox}>
              <input
                type="checkbox"
                checked={createdByMe}
                onChange={(e) => onCreatedByMeChange(e.target.checked)}
              />
              <span>Created by</span>
              <span className={styles.checkboxLabel}>Me</span>
            </label>
            <select
              className={styles.select}
              value={selectedCreator}
              onChange={(e) => onCreatorChange?.(e.target.value)}
            >
              <option value="">Created By</option>
            </select>
          </div>
        </div>

        <div className={styles.filterGroup}>
          <button className={styles.filterHeader}>
            <ChevronDown size={14} />
            <span>SHARED</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssistantsSidebar;
